/**
 * Security Admin Dashboard Component
 *
 * Comprehensive security management interface for administrators.
 * Features:
 * - IP Address Blacklisting System
 * - Wallet Address Blacklisting System
 * - Wallet Address Uniqueness Monitoring
 * - Account Lockouts (v7.3.372 — durable per-user login lockout from
 *   lib/loginSecurity.js; admins can release a locked user without SQL)
 * - Security Event Logs
 * - Real-time threat indicators
 */

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { getSessionHeaders } from '../../lib/serverSession';
import LoginLockoutManager from './LoginLockoutManager';

interface BlockedIP {
  id: string;
  ip_address: string;
  reason: string;
  blocked_by: string;
  associated_user_ids: number[];
  associated_usernames: string[];
  blocked_at: string;
  expires_at: string | null;
  is_active: boolean;
  block_type: string;
  metadata: any;
}

interface BlockedWallet {
  id: string;
  wallet_address: string;
  network: string;
  reason: string;
  blocked_by: string;
  associated_user_ids: number[];
  associated_usernames: string[];
  total_fraud_amount: number;
  blocked_at: string;
  is_active: boolean;
  block_type: string;
  metadata: any;
}

interface LockedAccount {
  id: number;
  username: string | null;
  email: string;
  full_name: string | null;
  failed_login_attempts: number;
  lockout_until: string | null;
  last_known_ip: string | null;
  last_failed_login_at: string | null;
  last_failed_login_ip: string | null;
  trusted_ips: string[] | null;
}

interface SecurityStats {
  blockedIPs: number;
  blockedWallets: number;
  recentAttempts: number;
  totalFraudAmount: number;
  lockedAccounts: number;
}

interface SecurityAdminDashboardProps {
  currentUser?: any;
}

const SecurityAdminDashboard: React.FC<SecurityAdminDashboardProps> = () => {
  const [activeTab, setActiveTab] = useState<'ips' | 'wallets' | 'lockouts' | 'logs'>('ips');
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([]);
  const [blockedWallets, setBlockedWallets] = useState<BlockedWallet[]>([]);
  const [lockedAccounts, setLockedAccounts] = useState<LockedAccount[]>([]);
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  
  // Form states
  const [newIPAddress, setNewIPAddress] = useState('');
  const [newIPReason, setNewIPReason] = useState('');
  const [newWalletAddress, setNewWalletAddress] = useState('');
  const [newWalletReason, setNewWalletReason] = useState('');
  const [newWalletFraudAmount, setNewWalletFraudAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token}`,
      ...getSessionHeaders()
    };
  };

  const fetchBlockedIPs = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/admin/security/blocked-ips?includeInactive=${showInactive}`, { headers });
      const data = await response.json();
      if (data.success) {
        setBlockedIPs(data.data || []);
      }
    } catch (err: any) {
      console.error('Error fetching blocked IPs:', err);
    }
  }, [showInactive]);

  const fetchBlockedWallets = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/admin/security/blocked-wallets?includeInactive=${showInactive}`, { headers });
      const data = await response.json();
      if (data.success) {
        setBlockedWallets(data.data || []);
      }
    } catch (err: any) {
      console.error('Error fetching blocked wallets:', err);
    }
  }, [showInactive]);

  const fetchLockouts = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/admin/security/account-lockouts', { headers });
      const data = await response.json();
      if (data.success) {
        setLockedAccounts(data.data || []);
      }
    } catch (err: any) {
      console.error('Error fetching account lockouts:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    const totalFraud = blockedWallets.reduce((sum, w) => sum + (w.total_fraud_amount || 0), 0);
    const nowTs = Date.now();
    const activeLockouts = lockedAccounts.filter(
      a => a.lockout_until && new Date(a.lockout_until).getTime() > nowTs
    ).length;
    setStats({
      blockedIPs: blockedIPs.filter(ip => ip.is_active).length,
      blockedWallets: blockedWallets.filter(w => w.is_active).length,
      recentAttempts: 0,
      totalFraudAmount: totalFraud,
      lockedAccounts: activeLockouts
    });
  }, [blockedIPs, blockedWallets, lockedAccounts]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBlockedIPs(), fetchBlockedWallets(), fetchLockouts()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBlockedIPs, fetchBlockedWallets, fetchLockouts]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleBlockIP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newIPAddress || !newIPReason) return;
    setActionLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/admin/security/blocked-ips', {
        method: 'POST',
        headers,
        body: JSON.stringify({ ipAddress: newIPAddress, reason: newIPReason, blockType: 'manual' })
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`IP ${newIPAddress} blocked successfully`);
        setNewIPAddress('');
        setNewIPReason('');
        fetchBlockedIPs();
      } else {
        setError(data.error || 'Failed to block IP');
      }
    } catch (err: any) {
      setError(err.message);
    }
    setActionLoading(false);
  };

  const handleUnblockIP = async (ipAddress: string) => {
    if (!confirm(`Are you sure you want to unblock IP ${ipAddress}?`)) return;
    setActionLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/admin/security/blocked-ips', {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ ipAddress, reason: 'Manual unblock by admin' })
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`IP ${ipAddress} unblocked successfully`);
        fetchBlockedIPs();
      } else {
        setError(data.error || 'Failed to unblock IP');
      }
    } catch (err: any) {
      setError(err.message);
    }
    setActionLoading(false);
  };

  const handleBlockWallet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWalletAddress || !newWalletReason) return;
    setActionLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/admin/security/blocked-wallets', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          walletAddress: newWalletAddress,
          reason: newWalletReason,
          blockType: 'manual',
          fraudAmount: parseFloat(newWalletFraudAmount) || 0
        })
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`Wallet ${newWalletAddress.substring(0,10)}... blocked successfully`);
        setNewWalletAddress('');
        setNewWalletReason('');
        setNewWalletFraudAmount('');
        fetchBlockedWallets();
      } else {
        setError(data.error || 'Failed to block wallet');
      }
    } catch (err: any) {
      setError(err.message);
    }
    setActionLoading(false);
  };

  const handleUnblockWallet = async (walletAddress: string) => {
    if (!confirm(`Are you sure you want to unblock wallet ${walletAddress.substring(0,10)}...?`)) return;
    setActionLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/admin/security/blocked-wallets', {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ walletAddress, reason: 'Manual unblock by admin' })
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`Wallet unblocked successfully`);
        fetchBlockedWallets();
      } else {
        setError(data.error || 'Failed to unblock wallet');
      }
    } catch (err: any) {
      setError(err.message);
    }
    setActionLoading(false);
  };

  const handleClearLockout = async (account: LockedAccount) => {
    const label = account.username || account.email;
    const reason = window.prompt(`Clear account lockout for ${label}?\n\nOptional reason for the audit log:`, '');
    if (reason === null) return;
    setActionLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/admin/security/account-lockouts', {
        method: 'POST',
        headers,
        body: JSON.stringify({ userId: account.id, reason: reason.trim() || null })
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`Lockout cleared for ${label}`);
        fetchLockouts();
      } else {
        setError(data.error || 'Failed to clear lockout');
      }
    } catch (err: any) {
      setError(err.message);
    }
    setActionLoading(false);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  const isLockoutActive = (lockoutUntil: string | null): boolean => {
    return Boolean(lockoutUntil) && new Date(lockoutUntil as string).getTime() > Date.now();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-900 text-white min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-yellow-500 flex items-center gap-2">
          🛡️ Security Admin Dashboard
        </h1>
        <p className="text-white mt-1">Manage blocked IPs, wallets, and security monitoring</p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-900/50 border border-green-500 rounded-lg text-green-400">
          ✅ {successMessage}
          <button onClick={() => setSuccessMessage('')} className="float-right">×</button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-400">
          ❌ {error}
          <button onClick={() => setError(null)} className="float-right">×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-3xl font-bold text-red-500">{stats?.blockedIPs || 0}</div>
          <div className="text-white text-sm">Blocked IPs</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-3xl font-bold text-orange-500">{stats?.blockedWallets || 0}</div>
          <div className="text-white text-sm">Blocked Wallets</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-3xl font-bold text-purple-400">{stats?.lockedAccounts || 0}</div>
          <div className="text-white text-sm">Locked Accounts</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-3xl font-bold text-yellow-500">${stats?.totalFraudAmount?.toLocaleString() || 0}</div>
          <div className="text-white text-sm">Total Fraud Amount</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-700 pb-2">
        <button
          onClick={() => setActiveTab('ips')}
          className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === 'ips' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
        >
          🌐 Blocked IPs ({blockedIPs.filter(ip => ip.is_active).length})
        </button>
        <button
          onClick={() => setActiveTab('wallets')}
          className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === 'wallets' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
        >
          💳 Blocked Wallets ({blockedWallets.filter(w => w.is_active).length})
        </button>
        <button
          onClick={() => setActiveTab('lockouts')}
          className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === 'lockouts' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
        >
          🔒 Account Lockouts ({lockedAccounts.filter(a => isLockoutActive(a.lockout_until)).length})
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === 'logs' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
        >
          📋 Security Logs
        </button>
        <label className="ml-auto flex items-center gap-2 text-sm text-white">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="rounded" />
          Show inactive
        </label>
      </div>

      {/* IP Blocking Tab */}
      {activeTab === 'ips' && (
        <div>
          {/* Add IP Form */}
          <form onSubmit={handleBlockIP} className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-yellow-500 mb-4">Block New IP Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="IP Address (e.g., 192.168.1.1)"
                value={newIPAddress}
                onChange={(e) => setNewIPAddress(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                required
              />
              <input
                type="text"
                placeholder="Reason for blocking"
                value={newIPReason}
                onChange={(e) => setNewIPReason(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                required
              />
              <button
                type="submit"
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50"
              >
                {actionLoading ? 'Blocking...' : '🚫 Block IP'}
              </button>
            </div>
          </form>

          {/* IP Table */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">IP Address</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Reason</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Blocked At</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Associated Users</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {blockedIPs.map((ip) => (
                  <tr key={ip.id} className={`border-t border-gray-700 ${!ip.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 font-mono text-red-400">{ip.ip_address}</td>
                    <td className="px-4 py-3 text-gray-300">{ip.reason}</td>
                    <td className="px-4 py-3 text-white text-sm">{formatDate(ip.blocked_at)}</td>
                    <td className="px-4 py-3 text-gray-300">
                      {ip.associated_usernames?.join(', ') || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${ip.is_active ? 'bg-red-900 text-red-300' : 'bg-gray-600 text-gray-300'}`}>
                        {ip.is_active ? 'BLOCKED' : 'INACTIVE'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {ip.is_active && (
                        <button
                          onClick={() => handleUnblockIP(ip.ip_address)}
                          className="text-green-400 hover:text-green-300 text-sm"
                        >
                          ✓ Unblock
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {blockedIPs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No blocked IPs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Wallet Blocking Tab */}
      {activeTab === 'wallets' && (
        <div>
          {/* Add Wallet Form */}
          <form onSubmit={handleBlockWallet} className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-yellow-500 mb-4">Block New Wallet Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="Wallet Address (0x...)"
                value={newWalletAddress}
                onChange={(e) => setNewWalletAddress(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white font-mono text-sm"
                required
              />
              <input
                type="text"
                placeholder="Reason for blocking"
                value={newWalletReason}
                onChange={(e) => setNewWalletReason(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                required
              />
              <input
                type="number"
                placeholder="Fraud Amount ($)"
                value={newWalletFraudAmount}
                onChange={(e) => setNewWalletFraudAmount(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
              />
              <button
                type="submit"
                disabled={actionLoading}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50"
              >
                {actionLoading ? 'Blocking...' : '🚫 Block Wallet'}
              </button>
            </div>
          </form>

          {/* Wallet Table */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Wallet Address</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Reason</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Fraud Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Associated Users</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {blockedWallets.map((wallet) => (
                  <tr key={wallet.id} className={`border-t border-gray-700 ${!wallet.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-orange-400 text-sm">{wallet.wallet_address.substring(0,10)}...{wallet.wallet_address.substring(38)}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-300">{wallet.reason}</td>
                    <td className="px-4 py-3 text-red-400 font-bold">${wallet.total_fraud_amount?.toLocaleString() || 0}</td>
                    <td className="px-4 py-3 text-gray-300 text-sm">
                      {wallet.associated_usernames?.join(', ') || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${wallet.is_active ? 'bg-orange-900 text-orange-300' : 'bg-gray-600 text-gray-300'}`}>
                        {wallet.is_active ? 'BLOCKED' : 'INACTIVE'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {wallet.is_active && (
                        <button
                          onClick={() => handleUnblockWallet(wallet.wallet_address)}
                          className="text-green-400 hover:text-green-300 text-sm"
                        >
                          ✓ Unblock
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {blockedWallets.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No blocked wallets found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Account Lockouts Tab — same Johan reset surface as Admin → Login Lockouts */}
      {activeTab === 'lockouts' && (
        <div className="-mx-6 -mb-6">
          <LoginLockoutManager />
        </div>
      )}

      {/* Security Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-yellow-500 mb-4">Recent Security Events</h3>
          <p className="text-white">Security event logs will be displayed here. Check the main Audit Log for detailed security events.</p>
        </div>
      )}
    </div>
  );
};

export default SecurityAdminDashboard;
